import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, StarIcon } from '@heroicons/react/24/solid';

/**
 * Чат клуба с разметкой заявок — экран разбора качества (round 25).
 *  Одна лента чата за период; поверх неё — события заявок (создана / первый
 *  ответ / решена / закрыта / оценка) серыми плашками. Слева — заявки периода;
 *  клик подсвечивает окно заявки в ленте и прокручивает к её началу.
 *  ?ticket=<id> открывает окно конкретной заявки (ссылка из карточки/окна).
 */

interface TLTicket {
  id: string; ticketNumber: string; description?: string; status: string; priority?: string; supportLine?: number;
  assignedTo?: { firstName?: string; lastName?: string } | null;
  resolvedBy?: { firstName?: string; lastName?: string } | null;
  createdAt: string; firstResponseTime?: string | null; resolutionTime?: string | null; closedAt?: string | null;
  resolutionNote?: string | null;
  csat?: { rating?: number | null; comment?: string | null; respondedAt?: string | null } | null;
}
interface TLMessage {
  id: string; ticketId: string; ticketNumber?: string | null; sender: string; senderName?: string; content?: string;
  messageType?: string; mediaUrl?: string; fileName?: string; timestamp?: string; createdAt?: string;
  replyTo?: { senderName?: string; content?: string } | null;
}
type Item =
  | { kind: 'msg'; at: number; m: TLMessage }
  | { kind: 'ev'; at: number; icon: string; text: string; ticket: TLTicket; tone: 'muted' | 'good' | 'bad' | 'warn' };

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hm = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '');
const dayLabel = (ts: number) => {
  const d = new Date(ts); const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return 'Сегодня';
  if (d.toDateString() === y.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', ...(d.getFullYear() !== t.getFullYear() ? { year: 'numeric' } : {}) });
};
const fmtDur = (a?: string | null, b?: string | null) => {
  if (!a || !b) return '';
  const s = Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000));
  if (s < 60) return `${s} с`;
  const m = Math.floor(s / 60); if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60); return `${h} ч ${m % 60} мин`;
};
const shortNum = (n?: string | null) => (n || '').replace(/^TKT-\d{4}-0*/, '') || (n || '');
const who = (u?: { firstName?: string; lastName?: string } | null) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');
const STATUS_RU: Record<string, string> = { new: 'Новая', assigned: 'Назначена', in_progress: 'В работе', paused: 'На паузе', resolved: 'Решена', closed: 'Закрыта' };
const MEDIA_LABEL: Record<string, string> = { photo: '📷 Фото', document: '📄 Файл', voice: '🎤 Голосовое', audio: '🎵 Аудио', video: '🎬 Видео', sticker: 'Стикер' };
const TONE: Record<string, string> = {
  muted: 'bg-muted text-muted-foreground', good: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  bad: 'bg-red-500/10 text-red-700 dark:text-red-400', warn: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
};

const ClubChatPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{ title: string; messages: TLMessage[]; tickets: TLTicket[]; from: string; to: string; focusTicketId?: string | null; truncated?: boolean } | null>(null);
  const [selected, setSelected] = useState<string | null>(sp.get('ticket'));
  const [from, setFrom] = useState(sp.get('from') || '');
  const [to, setTo] = useState(sp.get('to') || '');
  const feedRef = useRef<HTMLDivElement | null>(null);
  const anchorRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const load = async (params: Record<string, string>) => {
    if (!chatId) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/tickets/clients/${encodeURIComponent(chatId)}/timeline`, { params });
      const d = res.data?.data;
      setData(d);
      if (!params.ticket) {
        setFrom(ymd(new Date(d.from))); setTo(ymd(new Date(new Date(d.to).getTime() - 3600_000)));
      } else {
        setFrom(ymd(new Date(d.from))); setTo(ymd(new Date(d.to)));
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить чат клуба');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = sp.get('ticket');
    if (t) load({ ticket: t }); else load({ ...(sp.get('from') ? { from: sp.get('from')! } : {}), ...(sp.get('to') ? { to: sp.get('to')! } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  const apply = () => {
    const next: Record<string, string> = {}; if (from) next.from = from; if (to) next.to = to;
    setSp(next); setSelected(null); load(next);
  };

  const items = useMemo<Item[]>(() => {
    if (!data) return [];
    const out: Item[] = data.messages.map((m) => ({ kind: 'msg', at: new Date(m.timestamp || m.createdAt || 0).getTime(), m }));
    for (const t of data.tickets) {
      const n = shortNum(t.ticketNumber);
      out.push({ kind: 'ev', at: new Date(t.createdAt).getTime() - 1, icon: '🎫', tone: 'muted', ticket: t, text: `Заявка ${n} создана: ${(t.description || '').slice(0, 140)}` });
      if (t.firstResponseTime) out.push({ kind: 'ev', at: new Date(t.firstResponseTime).getTime() + 1, icon: '⚡', tone: 'muted', ticket: t, text: `Первый ответ по ${n} через ${fmtDur(t.createdAt, t.firstResponseTime)}` });
      if (t.resolutionTime) out.push({ kind: 'ev', at: new Date(t.resolutionTime).getTime() + 2, icon: '✅', tone: 'good', ticket: t, text: `Заявка ${n} решена${who(t.resolvedBy) ? ` (${who(t.resolvedBy)})` : ''} за ${fmtDur(t.createdAt, t.resolutionTime)}${t.resolutionNote ? `: ${t.resolutionNote}` : ''}` });
      if (t.closedAt) out.push({ kind: 'ev', at: new Date(t.closedAt).getTime() + 3, icon: '🔒', tone: 'muted', ticket: t, text: `Заявка ${n} закрыта` });
      if (t.csat?.rating) out.push({ kind: 'ev', at: new Date(t.csat.respondedAt || t.closedAt || t.resolutionTime || t.createdAt).getTime() + 4, icon: '⭐', tone: t.csat.rating <= 2 ? 'bad' : t.csat.rating === 3 ? 'warn' : 'good', ticket: t, text: `Оценка по ${n}: ${t.csat.rating}/5${t.csat.comment ? ` — «${t.csat.comment}»` : ''}` });
    }
    return out.sort((a, b) => a.at - b.at);
  }, [data]);

  const sel = useMemo(() => data?.tickets.find((t) => t.id === selected) || null, [data, selected]);
  const inWindow = (ts: number) => {
    if (!sel) return false;
    const a = new Date(sel.createdAt).getTime() - 60_000;
    const b = sel.closedAt || sel.resolutionTime ? new Date((sel.closedAt || sel.resolutionTime) as string).getTime() + 15 * 60_000 : Infinity;
    return ts >= a && ts <= b;
  };

  useEffect(() => {
    if (!selected || loading) return;
    const el = anchorRefs.current[selected];
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selected, loading, data]);

  const pick = (id: string) => { setSelected(id); };

  return (
    <div className="flex h-[calc(100dvh-8rem)] sm:h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-6.5rem)] overflow-hidden rounded-lg border border-border bg-card">
      {/* Заявки периода */}
      <aside className="hidden md:flex w-80 shrink-0 flex-col border-r border-border">
        <div className="px-3 py-2 border-b border-border flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1 rounded text-muted-foreground hover:text-foreground"><ArrowLeftIcon className="h-5 w-5" /></button>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{data?.title || 'Чат клуба'}</div>
            <div className="text-[11px] text-muted-foreground">разбор: заявки и переписка</div>
          </div>
        </div>
        <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 text-xs">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 px-2 rounded border border-input bg-background flex-1 min-w-0" />
          <span className="text-muted-foreground">—</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 px-2 rounded border border-input bg-background flex-1 min-w-0" />
          <button onClick={apply} className="h-8 px-2.5 rounded bg-primary text-primary-foreground">OK</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {(data?.tickets || []).length === 0 && !loading && <div className="p-4 text-xs text-muted-foreground text-center">Заявок за период нет</div>}
          {(data?.tickets || []).map((t) => {
            const active = t.id === selected;
            const r = t.csat?.rating || 0;
            return (
              <button key={t.id} onClick={() => pick(t.id)} className={`w-full text-left px-3 py-2 border-b border-border/60 transition-colors ${active ? 'bg-primary/10' : 'hover:bg-accent/50'}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold">{shortNum(t.ticketNumber)}</span>
                  <span className="text-muted-foreground">{STATUS_RU[t.status] || t.status}</span>
                  {r > 0 && (
                    <span className={`ml-auto inline-flex items-center gap-0.5 ${r <= 2 ? 'text-red-500' : r === 3 ? 'text-amber-500' : 'text-emerald-500'}`}>
                      <StarIcon className="h-3.5 w-3.5" />{r}
                    </span>
                  )}
                </div>
                <div className="text-xs text-foreground/90 line-clamp-2 mt-0.5">{t.description || '—'}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {new Date(t.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  {t.firstResponseTime && <> · реакция {fmtDur(t.createdAt, t.firstResponseTime)}</>}
                  {t.resolutionTime && <> · решена за {fmtDur(t.createdAt, t.resolutionTime)}</>}
                  {who(t.assignedTo) && <> · {who(t.assignedTo)}</>}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Лента */}
      <section className="flex-1 min-w-0 flex flex-col bg-muted/20">
        <div className="px-3 py-2 border-b border-border bg-card flex items-center gap-3 text-xs">
          <button onClick={() => navigate(-1)} className="md:hidden p-1 text-muted-foreground"><ArrowLeftIcon className="h-5 w-5" /></button>
          <span className="font-semibold text-sm truncate">{data?.title || ''}</span>
          {sel && (
            <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              окно заявки {shortNum(sel.ticketNumber)}
              <button onClick={() => setSelected(null)} className="opacity-70 hover:opacity-100">✕</button>
            </span>
          )}
          {data?.truncated && <span className="text-amber-600">показаны первые 3000 сообщений — сузьте период</span>}
          <span className="ml-auto flex items-center gap-2">
            {chatId && <Link to={`/clients/${encodeURIComponent(chatId)}`} className="text-primary hover:underline">карточка клиента</Link>}
            {sel && <Link to={`/tickets/${sel.id}`} className="text-primary hover:underline">карточка заявки</Link>}
          </span>
        </div>
        <div ref={feedRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1.5">
          {loading ? (
            <div className="p-8 text-center text-xs text-muted-foreground"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2" />Загрузка…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-xs text-muted-foreground">За период сообщений нет</div>
          ) : items.map((it, i) => {
            const prev = items[i - 1];
            const showDay = !prev || new Date(prev.at).toDateString() !== new Date(it.at).toDateString();
            const hl = inWindow(it.at);
            return (
              <React.Fragment key={it.kind === 'msg' ? it.m.id : `${it.ticket.id}-${it.icon}-${it.at}`}>
                {showDay && <div className="flex justify-center my-2"><span className="px-3 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground">{dayLabel(it.at)}</span></div>}
                {it.kind === 'ev' ? (
                  <div
                    ref={(el) => { if (it.icon === '🎫') anchorRefs.current[it.ticket.id] = el; }}
                    className={`flex justify-center ${hl ? 'bg-primary/5 rounded-lg' : ''}`}
                  >
                    <button onClick={() => pick(it.ticket.id)} className={`max-w-[92%] text-left px-3 py-1 rounded-2xl text-[11px] whitespace-pre-wrap break-words ${TONE[it.tone]} ${selected === it.ticket.id ? 'ring-1 ring-primary/50' : ''}`}>
                      {it.icon} {it.text} <span className="opacity-60">· {hm(new Date(it.at).toISOString())}</span>
                    </button>
                  </div>
                ) : (() => {
                  const m = it.m; const op = m.sender === 'operator'; const sys = m.senderName === 'Система';
                  if (sys) return (
                    <div className={`flex justify-center ${hl ? 'bg-primary/5 rounded-lg' : ''}`}>
                      <span className="max-w-[90%] text-center px-3 py-1 rounded-2xl bg-muted/70 text-[11px] text-muted-foreground whitespace-pre-wrap break-words">{m.content}</span>
                    </div>
                  );
                  const body = m.messageType && m.messageType !== 'text' ? `${MEDIA_LABEL[m.messageType] || m.messageType}${m.content ? ` ${m.content}` : ''}` : (m.content || '');
                  return (
                    <div className={`flex ${op ? 'justify-end' : 'justify-start'} ${hl ? 'bg-primary/5 rounded-lg' : ''}`}>
                      <div className={`max-w-[80%] px-3 py-2 shadow text-sm ${op ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md' : 'bg-card text-foreground rounded-2xl rounded-bl-md'}`}>
                        <div className="flex items-center gap-2 text-[11px] font-semibold opacity-80 mb-0.5">
                          <span>{m.senderName || (op ? 'Оператор' : 'Клиент')}</span>
                          {m.ticketNumber && (
                            <button onClick={() => pick(m.ticketId)} className={`text-[10px] px-1.5 rounded-full ${op ? 'bg-white/20' : 'bg-muted'} ${selected === m.ticketId ? 'ring-1 ring-primary/60' : ''}`} title="Заявка, к которой привязано сообщение">
                              {shortNum(m.ticketNumber)}
                            </button>
                          )}
                        </div>
                        {m.replyTo && <div className={`border-l-2 pl-2 mb-1 text-[11px] opacity-90 ${op ? 'border-primary-foreground/60' : 'border-primary'}`}><div className="truncate">↪ {m.replyTo.senderName}: {m.replyTo.content || '…'}</div></div>}
                        {m.messageType === 'photo' && m.mediaUrl && <img src={m.mediaUrl} alt="" className="rounded mb-1 max-h-60 cursor-zoom-in" onClick={() => window.open(m.mediaUrl, '_blank')} />}
                        {body && <div className="whitespace-pre-wrap break-words">{body}</div>}
                        <div className={`text-[10px] mt-0.5 text-right ${op ? 'opacity-80' : 'text-muted-foreground'}`}>{hm(m.timestamp || m.createdAt)}</div>
                      </div>
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ClubChatPage;
